type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

interface SoundConfig {
  frequencies: number[];
  duration: number;
  type: OscillatorType;
}

const SOUND_CONFIGS: Record<Rarity, SoundConfig> = {
  common: {
    frequencies: [523], // C5
    duration: 200,
    type: 'sine',
  },
  rare: {
    frequencies: [523, 659], // C5, E5
    duration: 300,
    type: 'sine',
  },
  epic: {
    frequencies: [523, 659, 784], // C5, E5, G5 (C major chord)
    duration: 400,
    type: 'sine',
  },
  legendary: {
    frequencies: [523, 659, 784, 1047], // C5, E5, G5, C6 (C major with octave)
    duration: 600,
    type: 'sine',
  },
};

// Subtle click sound for menu interactions
export function playClickSound(): void {
  // Check user preference
  const soundEnabled = localStorage.getItem('sidebar-sound-enabled') !== 'false';
  if (!soundEnabled) return;

  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    // Subtle "pop" sound - high frequency, very short
    oscillator.frequency.value = 1200;
    oscillator.type = 'sine';

    // Envelope: quick attack, quick decay
    const now = audioContext.currentTime;
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.06, now + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.08);

    oscillator.start(now);
    oscillator.stop(now + 0.1);

    setTimeout(() => audioContext.close(), 150);
  } catch (error) {
    console.warn('Could not play click sound:', error);
  }
}

export function playAchievementSound(rarity: Rarity): void {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;

    const audioContext = new AudioContextClass();
    const config = SOUND_CONFIGS[rarity];
    const masterGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);
    masterGain.gain.setValueAtTime(0.15, audioContext.currentTime);

    config.frequencies.forEach((freq, i) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(masterGain);

      oscillator.frequency.value = freq;
      oscillator.type = config.type;

      const startTime = audioContext.currentTime + i * 0.08;
      const endTime = startTime + config.duration / 1000;

      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.01, endTime);

      oscillator.start(startTime);
      oscillator.stop(endTime + 0.1);
    });

    // Clean up after sound finishes
    setTimeout(() => {
      audioContext.close();
    }, config.duration + 500);
  } catch (error) {
    console.warn('Could not play achievement sound:', error);
  }
}
